import React, {Suspense, lazy} from 'react';
import PropTypes from 'prop-types';
import {useRoutes} from 'react-router-dom';
import {multiPathRoutes, notFoundRoute} from '~/router/routerHelpers';

const AddTextCard_Lg = lazy(
  () => import('~/components/AddTextCard_Lg'),
  'AddTextCard_Lg'
);
const ArtTools = lazy(() => import('~/components/ArtTools'), 'ArtTools');
const FontOverlay = lazy(
  () => import('~/components/FontOverlay'),
  'FontOverlay'
);
const IconColorPicker = lazy(
  () => import('~/components/IconColorPicker'),
  'IconColorPicker'
);
const IconSpotColorPicker = lazy(
  () => import('~/components/IconSpotColorPicker'),
  'IconSpotColorPicker'
);
const PrintMethodCard = lazy(
  () => import('~/components/PrintMethodCard'),
  'PrintMethodCard'
);
const RedrawsCard = lazy(
  () => import('~/components/RedrawsCard'),
  'RedrawsCard'
);
const TextCard_Lg = lazy(
  () => import('~/components/TextCard_Lg'),
  'TextCard_Lg'
);
const UploadBrowseArt = lazy(
  () => import('~/components/UploadBrowseArt'),
  'UploadBrowseArt'
);
const UploadCard = lazy(() => import('~/components/UploadCard'), 'UploadCard');
const UploadTools = lazy(
  () => import('~/components/UploadTools'),
  'UploadTools'
);
const ProductColorPicker = lazy(
  () => import('~/components/ProductColorPicker'),
  'ProductColorPicker'
);
const TextEffectPicker = lazy(
  () => import('~/components/TextEffectPicker'),
  'TextEffectPicker'
);
const TextOutlineColorPicker = lazy(
  () => import('~/components/TextOutlineColorPicker'),
  'TextOutlineColorPicker'
);
const NamesAndNumbersCard = lazy(
  () => import('~/components/NamesAndNumbersCard'),
  'NamesAndNumbersCard'
);
const NamesAndNumbersTools = lazy(
  () => import('~/components/NamesAndNumbersTools'),
  'NamesAndNumbersTools'
);
const SavedDesignsOverlay = lazy(
  () => import('~/components/SavedDesignsOverlay'),
  'SavedDesignsOverlay'
);
const MyArts = lazy(() => import('~/components/MyArts'), 'MyArts');
const HelpMenu = lazy(() => import('~/components/HelpMenu'), 'HelpMenu');
const MultipleSelectionDisclaimer = lazy(
  () => import('~/components/MultipleSelectionDisclaimer'),
  'MultipleSelectionDisclaimer'
);
const SaveConfirmation = lazy(
  () => import('~/components/SaveConfirmation'),
  'SaveConfirmation'
);
const SaveForm = lazy(() => import('~/components/SaveForm'), 'SaveForm');
const ShareEmailForm = lazy(
  () => import('~/components/ShareEmailForm'),
  'ShareEmailForm'
);
const ArtIconSpotColorPicker = lazy(
  () => import('~/components/ArtIconSpotColorPicker'),
  'ArtIconSpotColorPicker'
);
const IconsArtSelectionCard = lazy(
  () => import('~/components/IconsArtSelectionCard'),
  'IconsArtSelectionCard'
);

// Eager loaded components
import ContentCard from '~/components/ContentCard';
import Panel from '~/components/Panel';
import ProgressIndicator from '~/components/ProgressIndicator';
import WelcomeContainer from '~/components/WelcomeContainer';
import CropCard from '~/components/CropCard';

const propTypes = {
  isFundraisingMode: PropTypes.bool
};

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const IconToolCardRoutesLg = props => {
  const element = useRoutes([
    {path: '/addText', element: <AddTextCard_Lg />},
    {path: '/artTools', element: <ArtTools {...props} />},
    ...multiPathRoutes({
      path: ['/addArt', '/addArt/*', '/searchArt/:searchTerm'],
      element: <IconsArtSelectionCard isLarge />
    }),
    {path: 'artTools/colors', element: <IconColorPicker />},
    ...multiPathRoutes({
      path: ['/changeArt', '/changeArt/*', '/searchArt/:searchTerm'],
      element: <IconsArtSelectionCard isLarge displayBackNavigation />
    }),
    {path: 'redraws', element: <RedrawsCard />},
    ...multiPathRoutes({
      path: ['/uploadForm', '/artAndUploadTools/uploadForm'],
      element: <UploadCard />
    }),
    {path: '/artAndUploadTools', element: <UploadBrowseArt />},
    ...multiPathRoutes({
      path: ['/welcome', '/startFromDesign'],
      element: <WelcomeContainer isFundraisingMode={props.isFundraisingMode} />
    }),
    {
      path: 'uploadTools',
      children: [
        {index: true, element: <UploadTools />},
        {path: 'spotColors', element: <IconSpotColorPicker />},
        {path: 'spotColors/:hexString', element: <IconSpotColorPicker />},
        {path: 'colors', element: <IconColorPicker />},
        {path: 'crop', element: <CropCard />}
      ]
    },
    {
      path: 'uploadProgress',
      element: <ProgressIndicator isLarge />
    },
    {
      path: 'productColor',
      element: <ProductColorPicker />
    },
    {
      path: 'printMethod',
      element: <PrintMethodCard />
    },
    ...multiPathRoutes({
      path: ['/artTools/spotColors', '/artTools/spotColors/:hexString'],
      element: <ArtIconSpotColorPicker />
    }),
    {
      path: 'namesAndNumbers',
      children: [
        {
          index: true,
          element: <NamesAndNumbersCard />
        },
        {
          path: 'tools',
          children: [
            {index: true, element: <NamesAndNumbersTools isLarge />},
            {
              path: 'colors',
              element: <IconColorPicker isPersonalization />
            }
          ]
        }
      ]
    },
    {
      path: 'textTools',
      children: [
        {
          element: <TextCard_Lg />,
          index: true
        },
        {
          element: <TextCard_Lg />,
          path: 'editText'
        },
        {path: 'textEffects', element: <TextEffectPicker largeBreakPoint />},
        {
          path: 'outline',
          element: <TextOutlineColorPicker largeBreakPoint />
        },
        {
          path: 'selectFont',
          children: [
            {
              index: true,
              element: <FontOverlay />
            },
            {
              path: ':category',
              element: <FontOverlay />
            }
          ]
        },
        {path: 'colors', element: <IconColorPicker />}
      ]
    },
    {
      path: 'help',
      element: <HelpMenu />
    },
    {
      path: 'selectIcon',
      element: <MultipleSelectionDisclaimer />
    },
    ...multiPathRoutes({
      path: ['/savedDesigns', '/startNewProject'],
      element: <SavedDesignsOverlay />
    }),
    {
      path: 'myUploads',
      element: <MyArts />
    },
    {
      path: 'saveConfirmation',
      element: <SaveConfirmation />
    },
    {
      path: 'gof',
      element: <SaveConfirmation gof />
    },
    {
      path: 'saveForm',
      element: <SaveForm />
    },
    {
      path: '/share/saveForm',
      element: <SaveForm />
    },
    {
      path: '/shareEmail',
      element: <ShareEmailForm />
    },
    {
      path: '/*',
      element: <WelcomeContainer isFundraisingMode={props.isFundraisingMode} />
    },
    notFoundRoute
  ]);

  return (
    <div className="ndx-Container ndx-Container--relative ndx-PanelContainer">
      <Panel>
        <Suspense fallback={<ContentCard />}>{element}</Suspense>
      </Panel>
    </div>
  );
};

IconToolCardRoutesLg.propTypes = propTypes;

export default IconToolCardRoutesLg;
